import { Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SITE_CONFIG, NAV_LINKS } from "@/config/site";

export const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const location = useLocation();

  const announcement = SITE_CONFIG.announcement;
  const showBanner = announcement?.enabled;

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [mobileMenuOpen]);

  const isActive = (href) => {
    if (href.startsWith("/#")) {
      return (
        location.pathname === "/" &&
        location.hash === href.substring(1)
      );
    }

    return location.pathname === href;
  };

  return (
    <>
      {/* Navbar Wrapper */}
      <div className="fixed top-0 left-0 right-0 z-50">
        {/* Announcement Banner */}
        {showBanner && (
          <div
            className="w-full adblk1 bg-gradient-to-r1 from-violet-600/401 via-indigo-600/401 to-violet-600/401 border-b border-white/101 bg-black/80 backdrop-blur-md"
            data-testid="announcement-banner"
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex flex-wrap items-center justify-center gap-2 sm:gap-3 text-center">
              <p className="text-xs sm:text-sm text-gray-200 mob360">
                {announcement.text}
              </p>

              {announcement.ctaLabel && (
                <Link
                  to={announcement.ctaHref || "#"}
                  className="inline-flex items-center gap-1 text-xs sm:text-sm font-medium qvpn-ft hover:text-white transition-colors duration-200"
                  data-testid="announcement-cta"
                >
                  {announcement.ctaLabel}
                  <ArrowRight className="w-3 h-3" />
                </Link>
              )}
            </div>
          </div>
        )}

        {/* Navbar */}
        <nav
          className={`transition-all duration-300 ${
            scrolled
              ? "bg-black/80 backdrop-blur-md border-b border-white/10"
              : "bg-transparent"
          }`}
          data-testid="navbar"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              {/* Logo */}
              <Link
                to="/"
                className="flex items-center gap-2"
                data-testid="logo"
              >
                <img
                  src="/QVPN_Logo_New.svg"
                  alt="QVPN Logo"
                  className="w-16 h-16 rounded-lg object-contain"
                />

                <span className="text-xl font-bold text-white">
                  {SITE_CONFIG.brand.name}
                </span>
              </Link>

              {/* Desktop Navigation */}
              <div className="hidden md:flex items-center gap-8">
                {NAV_LINKS.main.map((link) => (
                  <Link
                    key={link.name}
                    to={link.href}
                    className={`text-sm transition-colors duration-200 ${
                      isActive(link.href)
                        ? "text-white"
                        : "text-gray-400 hover:text-white"
                    }`}
                    data-testid={`nav-${link.name
                      .toLowerCase()
                      .replace(/\s/g, "-")}`}
                  >
                    {link.name}
                  </Link>
                ))}
              </div>

              {/* Desktop CTA */}
              <div className="hidden md:flex items-center gap-4">
                <Link to="/#download">
                <Button
                  className="btn-gradient-new"
                  data-testid="nav-get-started-btn"
                >
                  Get QVPN
                </Button>
                </Link>
              </div>

              {/* Mobile Menu Button */}
              <button
                className="md:hidden p-2 text-white"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-btn"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? (
                  <X className="w-7 h-7" />
                ) : (
                  <Menu className="w-7 h-7" />
                )}
              </button>
            </div>
          </div>
        </nav>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div
          className="md:hidden fixed inset-0 z-[999] bg-black backdrop-blur-lg"
          data-testid="mobile-menu"
        >
          {/* Mobile Header */}
          <div className="flex items-center justify-between px-6 h-16 border-b border-white/10">
            {/* Mobile Logo */}
            <Link
              to="/"
              className="flex items-center gap-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              <img
                src="/QVPN_Logo_New.svg"
                alt="QVPN Logo"
                className="w-12 h-12 rounded-lg object-contain"
              />

              <span className="text-lg font-bold text-white">
                {SITE_CONFIG.brand.name}
              </span>
            </Link>

            {/* Close Button */}
            <button
              className="p-2 text-gray-300 hover:text-white"
              onClick={() => setMobileMenuOpen(false)}
              aria-label="Close menu"
            >
              <X className="w-7 h-7" />
            </button>
          </div>

          {/* Mobile Navigation */}
          <div className="px-6 py-6">
            {NAV_LINKS.main.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className={`block py-4 text-lg border-b border-white/10 transition-colors duration-200 ${
                  isActive(link.href)
                    ? "text-white"
                    : "text-gray-300 hover:text-white"
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.name}
              </Link>
            ))}

            {/* Mobile CTA */}
            <div className="mt-8">
              <Link to="/#download">
              <Button className="w-full1 w-sm btn-gradient1 btn-gradient-new">
                Get QVPN
              </Button>
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;